/**
 * The Usage-screen chart ramp (#630 §7.3).
 *
 * A single six-step colour ramp, warm→cool, used as the one source of truth for
 * both composition bars (SpendCard) and the daily-leverage chart, so the two
 * are directly comparable. The design file used five untokenized hexes; here
 * every step is a gp-ui palette token (see @thegpvc/ui theme), resolved as a
 * CSS `var()` so it tracks the palette in light/dark mode.
 *
 * §7.3 sequence: orange-500 → orange-300 → orange-200 → a navy step → navy-100
 * → gray-200. The last (palest) step is reserved for the trailing residual
 * slice ("other" / "N more projects"); see `RAMP_RESIDUAL`.
 *
 * Follow-up: upstream these as `--chart-1 … --chart-6` in @gp/ui so future
 * charts share the palette (this repo only consumes @thegpvc/ui).
 */
export const CHART_RAMP = [
  'var(--color-orange-500)',
  'var(--color-orange-300)',
  'var(--color-orange-200)',
  'var(--color-navy-300)',
  'var(--color-navy-100)',
  'var(--color-gray-200)',
] as const;

// The palest step — the trailing residual slice ("other"/"N more projects").
export const RAMP_RESIDUAL = CHART_RAMP[CHART_RAMP.length - 1];

// Label colour for the residual slice (§4): a muted navy that still reads.
export const RAMP_RESIDUAL_LABEL = 'var(--color-navy-400)';

/**
 * Colour for the `i`th non-residual slice. Caps below the palest step so a
 * real slice never collides with the reserved residual colour.
 */
export function rampColor(i: number): string {
  return CHART_RAMP[Math.min(i, CHART_RAMP.length - 2)];
}

/**
 * Per-provider model ramps. One rank-ordered ramp would make Claude and GPT
 * models alternate through the same oranges and navies, and past five models
 * the cap above painted every remaining model the same pale navy, so the
 * stack was unreadable. Instead each provider family gets its own gradient,
 * dark → light in order of size: Claude models in orange, OpenAI/Codex models
 * in navy, anything else in gray.
 */
const MODEL_FAMILY_RAMPS = {
  claude: [
    'var(--color-orange-700)',
    'var(--color-orange-600)',
    'var(--color-orange-500)',
    'var(--color-orange-400)',
    'var(--color-orange-300)',
    'var(--color-orange-200)',
  ],
  openai: [
    'var(--color-navy-700)',
    'var(--color-navy-600)',
    'var(--color-navy-500)',
    'var(--color-navy-400)',
    'var(--color-navy-300)',
    'var(--color-navy-200)',
  ],
  other: ['var(--color-gray-500)', 'var(--color-gray-400)', 'var(--color-gray-300)'],
} as const;

export type ModelFamily = keyof typeof MODEL_FAMILY_RAMPS;

/**
 * The provider family of a model id: `claude-*` → claude; `gpt-*`, `codex-*`
 * (e.g. Codex's `codex-auto-review` guardian model) and o-series (`o3`,
 * `o4-mini`) → openai.
 */
export function modelFamily(model: string): ModelFamily {
  const m = model.toLowerCase();
  if (m.startsWith('claude-')) return 'claude';
  if (m.startsWith('gpt-') || m.startsWith('codex-') || /^o\d/.test(m)) return 'openai';
  return 'other';
}

/**
 * Colours for `models` (already ordered largest first): each model takes the
 * next step of its own family's ramp, capped at that ramp's palest step.
 */
export function modelColors(models: readonly string[]): Map<string, string> {
  const next: Record<ModelFamily, number> = { claude: 0, openai: 0, other: 0 };
  const colors = new Map<string, string>();
  for (const model of models) {
    if (colors.has(model)) continue;
    const family = modelFamily(model);
    const ramp = MODEL_FAMILY_RAMPS[family];
    colors.set(model, ramp[Math.min(next[family]++, ramp.length - 1)]);
  }
  return colors;
}
